import { Component, Prop, Host, h, Element, Event, EventEmitter, State, Method } from '@stencil/core';
import { KEY_CODES } from '../../utils/variables';

const VERTICAL_OFFSET = 12;

/**
 * A tooltip is a floating, non-actionable label that displays additional information about the interface element or feature.
 */

@Component({
  tag: 'som-tooltip',
  styleUrl: 'som-tooltip.scss',
})

export class SomTooltip {
  @Element() el: HTMLElement;
  forEl: HTMLElement;

  /**
   * This property shows the text to be displayed in the tooltip.
   */
  @Prop() content: string;

  /**
   * This property controls the horizontal position of the popover.
   */
  @Prop({ mutable: true }) horizontalPosition: 'left' | 'right' | 'auto' = 'auto';

  /**
   * 	This property controls the vertical position of the popover.
   */
  @Prop({ mutable: true }) verticalPosition: 'top' | 'bottom' | 'auto' = 'auto';

  /**
   * The tooltip role, set on the component by default.
   */
  //@Prop() role: string = 'tooltip';

  /**
   * The id for the active element to reference via aria-describedby.
   */
  @Prop() tooltipId: string;

  /**
   * This event is emitted when the popover is opened.
   */
  @Event() opened: EventEmitter;

  /**
   * This event is emitted when the popover is closed.
   */
  @Event() closed: EventEmitter;

  targetEl;

  handleFocusInCb;

  handleFocusOutCb;

  handleMouseOverCb;

  handleMouseOutCb;

  handleEscapeCb;

  handleTouchStartCb;

  handleTooltipMouseOverCb;

  handleTooltipMouseOutCb;

  @State() horizontalPos: string = this.horizontalPosition;
  @State() verticalPos: string = this.verticalPosition;

  /*
  * This method will close the popover.
  */
  @Method()
  open(): Promise<void> {
    return new Promise(resolve => {
      if (!this.forEl.classList.contains('visible')) {
        this.forEl.style.opacity = '1';
        this.forEl.style.display = 'inline';
        this.forEl.classList.add('visible');
        this.updatePosition();
        this.opened.emit();
        resolve();
      } else {
        // tooltip is already opened
        resolve();
      }
    });
  }

  /*
  * This method will close the popover.
  */
  @Method()
  close(): Promise<void> {
    return new Promise(resolve => {
      if (this.forEl.classList.contains('visible')) {
        this.forEl.style.opacity = '0';
        this.forEl.classList.remove('visible');
        this.closed.emit();
        resolve();
      } else {
        // tooltip is already closed
        resolve();
      }
    });
  }

  handleMouseOver() {
    this.open();
  }

  handleMouseOut() {
    this.close();
  }

  handleFocusIn() {
    this.open();
  }

  handleFocusOut() {
    this.close();
  }

  handleTouchStart() {
    this.open();
  }

  handleEscape(e) {
    if (this.targetEl && e.keyCode === KEY_CODES.ESC) {
      e.preventDefault();
      e.stopPropagation();
      this.close();
      this.closed.emit(e);
    }
  }

  handleTooltipMouseOver() {
    this.open();
  }

  handleTooltipMouseOut() {
    this.close();
  }

  registerEvents() {
    this.targetEl.addEventListener('focusin', this.handleFocusInCb);
    this.targetEl.addEventListener('focusout', this.handleFocusOutCb);
    this.targetEl.addEventListener('mouseover', this.handleMouseOverCb);
    this.targetEl.addEventListener('mouseout', this.handleMouseOutCb);
    this.targetEl.addEventListener('keydown', this.handleEscapeCb);
    this.targetEl.addEventListener('touchstart', this.handleTouchStartCb);

    this.forEl.addEventListener('mouseover', this.handleTooltipMouseOverCb);
    this.forEl.addEventListener('mouseout', this.handleTooltipMouseOutCb);
  }

  unregisterEvents() {
    this.targetEl.addEventListener('focusin', this.handleFocusInCb);
    this.targetEl.addEventListener('focusout', this.handleFocusOutCb);
    this.targetEl.addEventListener('mouseover', this.handleMouseOverCb);
    this.targetEl.addEventListener('mouseout', this.handleMouseOutCb);
    this.targetEl.addEventListener('keydown', this.handleEscapeCb);
    this.targetEl.addEventListener('touchstart', this.handleTouchStartCb);

    this.forEl.addEventListener('mouseover', this.handleTooltipMouseOverCb);
    this.forEl.addEventListener('mouseout', this.handleTooltipMouseOutCb);
  }

  updatePosition() {
    // Reset positioning
    this.forEl.style.top = '';
    this.forEl.style.bottom = '';
    this.forEl.style.left = '';
    this.forEl.style.right = '';
    this.horizontalPos = this.horizontalPosition;
    this.verticalPos = this.verticalPosition;

    // Reset caret
    this.forEl.classList.remove(
      "caret-top-left",
      "caret-top-right",
      "caret-bottom-left",
      "caret-bottom-right"
    );

    // Tooltip Element
    const tooltipRect = this.forEl.getBoundingClientRect();

    // Tooltip Trigger Element
    const tooltipTriggerRect = this.targetEl.getBoundingClientRect();
    
    const tooltipTriggerX = tooltipRect.x + tooltipRect.width;
    const tooltipTriggerY = tooltipTriggerRect.y + (tooltipTriggerRect.height + tooltipRect.height) * 2;

    // Horizontal Positioning
    if (this.horizontalPos === 'auto' || this.horizontalPos === null) {
      if (tooltipTriggerX > window.innerWidth) {
        this.horizontalPos = 'right';
        this.forEl.style.right = `${this.getRightPosition()}px`;
      } else {
        this.horizontalPos = 'left';
        this.forEl.style.left = `${this.getLeftPosition()}px`;
      }
    } else if (this.horizontalPos === 'right') {
      this.forEl.style.right = `${this.getRightPosition()}px`;
    } else {
      this.forEl.style.left = `${this.getLeftPosition()}px`;
    }

    // Vertical Positioning
    if (this.verticalPos === 'auto' || this.horizontalPos === null) {
      if (tooltipTriggerY > window.innerHeight) {
        this.verticalPos = 'top';
        this.forEl.style.bottom = `${this.getBottomPosition()}px`;
      } else {
        this.verticalPos = 'bottom';
        this.forEl.style.top = `${this.getTopPosition()}px`;
      }
    } else if (this.verticalPos === 'top') {
      this.forEl.style.bottom = `${this.getBottomPosition()}px`;
    } else {
      this.forEl.style.top = `${this.getTopPosition()}px`;
    }

    // Set caret direction
    this.forEl.classList.add(`caret-${this.verticalPos}-${this.horizontalPos}`);
  }

  getBottomPosition() {
    return this.targetEl.offsetParent.clientHeight - this.targetEl.offsetTop + VERTICAL_OFFSET;
  }

  getTopPosition() {
    return this.targetEl.offsetParent.clientTop + this.targetEl.offsetTop + this.targetEl.offsetHeight + VERTICAL_OFFSET;
  }

  getLeftPosition() {
    return this.targetEl.offsetParent.clientLeft + this.targetEl.offsetLeft - (this.targetEl.offsetWidth <= 24 ? 8 : 0);
  }

  getRightPosition() {
    return this.targetEl.offsetParent['offsetWidth'] - this.targetEl.offsetLeft - this.targetEl.offsetWidth - (this.targetEl.offsetWidth <= 24 ? 8 : 0);
  }

  componentWillLoad() {
    this.handleFocusInCb = this.handleFocusIn.bind(this);
    this.handleFocusOutCb = this.handleFocusOut.bind(this);
    this.handleMouseOverCb = this.handleMouseOver.bind(this);
    this.handleMouseOutCb = this.handleMouseOut.bind(this);
    this.handleEscapeCb = this.handleEscape.bind(this);
    this.handleTouchStartCb = this.handleTouchStart.bind(this);

    this.handleTooltipMouseOverCb = this.handleTooltipMouseOver.bind(this);
    this.handleTooltipMouseOutCb = this.handleTooltipMouseOut.bind(this);
  }

  componentDidLoad() {
    this.targetEl = document.querySelectorAll(`[aria-describedby="${this.tooltipId}"]`)[0];
    this.registerEvents();
  }

  componentWillUnload() {
    this.unregisterEvents();
  }

  render() {
    return (
      <Host
        id={this.tooltipId}
        role="tooltip"
        class="tooltip-wrapper"
        ref={(el) => this.forEl = el}>
          <slot>{this.content}</slot>
      </Host>
    );
  }
}
